import { FC, ReactNode } from "react";

import { TextVariantType } from "@/components/VnText/variants";
import { VnText } from "@/components/VnText/VnText";
import {
  VnTooltip,
  VnTooltipContent,
  VnTooltipTrigger,
} from "@/components/VnTooltip";
import { WorkRequirementType } from "@/domain/workRequirements";
import { I18nKeyType } from "@/i18n";
import { cn } from "@/utils/tailwind";

const LABEL_CLASS_NAME = "text-sidebar font-primary-semibold";

type WorkRequirementsLabelTooltipPropsType = {
  rule: WorkRequirementType["rule"];
  variant: TextVariantType;
  className?: string;
  // Filled in by <Trans> with the translated "Work Requirements" term.
  children?: ReactNode;
};

// The tooltip is rendered as text so the trigger stays inline within the
// translated sentence, wherever each language places the term.
const WorkRequirementsLabelTooltip: FC<
  WorkRequirementsLabelTooltipPropsType
> = ({ rule, variant, className, children }) => (
  <VnTooltip asChild>
    <VnText variant={variant} className={cn(className, LABEL_CLASS_NAME)}>
      <VnTooltipTrigger asChild>
        <VnText
          variant={variant}
          className={cn(
            className,
            LABEL_CLASS_NAME,
            "underline decoration-dotted",
          )}>
          {children}
        </VnText>
      </VnTooltipTrigger>
      <VnTooltipContent className="max-w-sm">
        <VnText
          variant="sm"
          className="text-center"
          i18nKey="native:workRequirements.whatAreDescription"
          i18nOptions={{
            hours: rule.requiredMonthlyHours,
            minAge: rule.minAge,
            maxAge: rule.maxAge,
          }}
        />
      </VnTooltipContent>
    </VnText>
  </VnTooltip>
);

type WorkRequirementsTitlePropsType = {
  i18nKey: I18nKeyType;
  i18nOptions?: object;
  variant?: TextVariantType;
  className?: string;
  // When given, the highlighted term explains work requirements on press.
  rule?: WorkRequirementType["rule"];
};

// Renders copy whose `<label>` tag marks the "Work Requirements" term, which
// design highlights in bold burgundy wherever it appears in a title.
export const WorkRequirementsTitle: FC<WorkRequirementsTitlePropsType> = ({
  i18nKey,
  i18nOptions,
  variant = "headerTwo",
  className,
  rule,
}) => (
  <VnText
    variant={variant}
    className={className}
    i18nKey={i18nKey}
    i18nOptions={i18nOptions}
    i18nProps={{
      components: {
        label: rule ? (
          <WorkRequirementsLabelTooltip
            rule={rule}
            variant={variant}
            className={className}
          />
        ) : (
          <VnText
            variant={variant}
            className={cn(className, LABEL_CLASS_NAME)}
          />
        ),
      },
    }}
  />
);
